/**
 * BehaviorRegistry — Resolves AbilityBuilder configs into behavior instances
 * and populates metadata for dashboards/introspection.
 *
 * Extracted from AbilityBuilder.build() to keep the builder class focused
 * on the fluent API surface.
 */

import type { AbilityMetadata } from './AbilityRegistry';
import type { ActionContext } from './types';
import type { AbilityBehavior } from './behaviors/types';
import type { AOEConfig } from './behaviors/AOEBehavior';
import type { DashConfig } from './behaviors/DashBehavior';
import type { ChannelConfig } from './behaviors/ChannelBehavior';
import type { HealConfig } from './behaviors/HealBehavior';
import type { ShieldConfig } from './behaviors/ShieldBehavior';
import type { DebuffConfig } from './behaviors/DebuffBehavior';
import type { MeleeConfig } from './behaviors/MeleeBehavior';
import type { HitscanConfig } from './behaviors/HitscanBehavior';
import type { SummonConfig } from './behaviors/SummonBehavior';
import type { NovaProjectileConfig } from './behaviors/NovaProjectileBehavior';
import type { AuraEffectConfig } from './behaviors/AuraEffectBehavior';
import type { ProjectileConfig, BuffConfig } from './AbilityBuilder';

import { gameLogger } from '../../../../utils/GameLogger';
import { EventBus } from '../../../../shared/EventBus';
import { describeCastGeometry, type GeometryExtras } from './castGeometry/describeCastGeometry';
import { conePlan, linePlan } from '../../../effects/castGeometry/castVisualPlan';
import { emitVisualPlan } from '../../../effects/castGeometry/emitVisualPlan';
import { playerCombatPoint } from '../../../../shared/logic/CombatAnchors';

const eventBus = EventBus.getInstance();

// Behavior classes are loaded on demand so that importing BehaviorRegistry does
// not eagerly pull in every behavior module (a source of large pre-room
// v8.evaluateModule stalls). Callers preload the subset they need before
// resolving abilities, or call loadAllBehaviors() before registering a class.
const behaviorClasses = new Map<string, new (...args: any[]) => any>();

export function registerBehaviorClass(name: string, ctor: new (...args: any[]) => any): void {
  behaviorClasses.set(name, ctor);
}

export function getBehaviorClass(name: string): new (...args: any[]) => any {
  let ctor = behaviorClasses.get(name);
  if (!ctor) {
    // Synchronous fallback for Node/jest tests where abilities are built at
    // module-load time before PreloadScene can preload behaviors. In the
    // browser this branch is skipped because `require` is undefined; the
    // PreloadScene path is the production loader.
    if (typeof require !== 'undefined') {
      const className = BEHAVIOR_CLASS_NAMES[name];
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const mod = require(`./behaviors/${className}`);
      ctor = mod[className];
      if (ctor) {
        behaviorClasses.set(name, ctor);
      }
    }
  }
  if (!ctor) {
    throw new Error(`Behavior "${name}" has not been loaded. Call loadBehavior/loadAllBehaviors before building abilities.`);
  }
  return ctor;
}

const BEHAVIOR_IMPORTS: Record<string, () => Promise<Record<string, any>>> = {
  projectile: () => import('./behaviors/ProjectileBehavior'),
  multiProjectile: () => import('./behaviors/MultiProjectileBehavior'),
  buff: () => import('./behaviors/BuffBehavior'),
  aoe: () => import('./behaviors/AOEBehavior'),
  dash: () => import('./behaviors/DashBehavior'),
  channel: () => import('./behaviors/ChannelBehavior'),
  heal: () => import('./behaviors/HealBehavior'),
  shield: () => import('./behaviors/ShieldBehavior'),
  debuff: () => import('./behaviors/DebuffBehavior'),
  melee: () => import('./behaviors/MeleeBehavior'),
  hitscan: () => import('./behaviors/HitscanBehavior'),
  summon: () => import('./behaviors/SummonBehavior'),
  novaProjectile: () => import('./behaviors/NovaProjectileBehavior'),
  auraEffect: () => import('./behaviors/AuraEffectBehavior'),
  timeline: () => import('./behaviors/TimelineBehavior'),
};

const BEHAVIOR_CLASS_NAMES: Record<string, string> = {
  projectile: 'ProjectileBehavior',
  multiProjectile: 'MultiProjectileBehavior',
  buff: 'BuffBehavior',
  aoe: 'AOEBehavior',
  dash: 'DashBehavior',
  channel: 'ChannelBehavior',
  heal: 'HealBehavior',
  shield: 'ShieldBehavior',
  debuff: 'DebuffBehavior',
  melee: 'MeleeBehavior',
  hitscan: 'HitscanBehavior',
  summon: 'SummonBehavior',
  novaProjectile: 'NovaProjectileBehavior',
  auraEffect: 'AuraEffectBehavior',
  timeline: 'TimelineBehavior',
};

export async function loadBehavior(name: string): Promise<void> {
  if (behaviorClasses.has(name)) return;
  const mod = await BEHAVIOR_IMPORTS[name]();
  const ctor = mod[BEHAVIOR_CLASS_NAMES[name]];
  if (!ctor) {
    throw new Error(`Behavior module "${name}" does not export class ${BEHAVIOR_CLASS_NAMES[name]}`);
  }
  registerBehaviorClass(name, ctor);
}

let allBehaviorsLoading: Promise<void> | null = null;

/**
 * Load every behavior once; concurrent and later callers share the same load. Ability modules
 * build at import time, so anything that imports one must await this first (2026-10-23: on a cold
 * start the learned-ability list arrived before PreloadScene finished, and BlinkAbility failed to
 * evaluate for the rest of the page).
 */
export function loadAllBehaviors(chunkSize = 3): Promise<void> {
  allBehaviorsLoading ??= loadBehaviorsInChunks(chunkSize).catch((error: unknown) => {
    allBehaviorsLoading = null;
    throw error;
  });
  return allBehaviorsLoading;
}

async function loadBehaviorsInChunks(chunkSize: number): Promise<void> {
  const names = Object.keys(BEHAVIOR_IMPORTS);
  for (let i = 0; i < names.length; i += chunkSize) {
    const chunk = names.slice(i, i + chunkSize);
    await Promise.all(chunk.map(loadBehavior));
    // Yield to the render loop between chunks to avoid a single long JS block
    // (a timer where there is no frame loop: Node tests and tools).
    if (i + chunkSize < names.length) {
      await new Promise<void>((resolve) => (typeof requestAnimationFrame === 'function'
        ? requestAnimationFrame(() => resolve())
        : setTimeout(resolve, 0)));
    }
  }
}

// =============================================================================
// CONFIG SHAPE — all the config fields the builder accumulates
// =============================================================================

export interface BuilderConfigs {
  instantEffect: ((ctx: ActionContext) => void) | null;
  buffConfigs: BuffConfig[];
  aoeConfig: AOEConfig | null;
  dashConfig: DashConfig | null;
  channelConfig: ChannelConfig | null;
  healConfig: HealConfig | null;
  shieldConfig: ShieldConfig | null;
  debuffConfig: DebuffConfig | null;
  meleeConfig: MeleeConfig | null;
  hitscanConfig: HitscanConfig | null;
  summonConfig: SummonConfig | null;
  novaConfig: NovaProjectileConfig | null;
  auraEffectConfig: AuraEffectConfig | null;
  coneAOEConfig: { damage: number | ((ctx: ActionContext) => number); angle: number; distance: number; ticks?: number; vfxId?: string } | null;
  lineAOEConfig: { damage: number | ((ctx: ActionContext) => number); length: number; width: number; ticks?: number; vfxId?: string } | null;
  multiProjectiles: { count: number; stagger: number; config: ProjectileConfig } | null;
  projectileConfig: ProjectileConfig | null;
  passiveConfig: { trigger: string; chance?: number; cooldownMs?: number; effect: (ctx: ActionContext) => void } | null;
  toggleConfig: { resourceDrainPerSecond?: number; onActivate?: (ctx: ActionContext) => void; onDeactivate?: (ctx: ActionContext) => void; maxDurationMs?: number } | null;
  windUpDuration: number;
}

// =============================================================================
// BEHAVIOR RESOLUTION
// =============================================================================

/**
 * Create behavior instances from the accumulated builder configs.
 * Order matters: behaviors execute in array order.
 */
export function resolveBehaviors(
  abilityId: string,
  configs: BuilderConfigs,
  _metadata: Partial<AbilityMetadata>,
): AbilityBehavior[] {
  const behaviors: AbilityBehavior[] = [];

  if (configs.instantEffect) {
    const effect = configs.instantEffect;
    behaviors.push({
      execute: (ctx) => {
        gameLogger.info('abilities', `${abilityId} activated`);
        effect(ctx);
      }
    });
  }

  if (configs.buffConfigs.length > 0) {
    // When any buff authors explicit VFX, it owns presentation and the other
    // stat buffs stay visual-silent. Otherwise only the first buff emits the
    // shared metadata aura. This preserves every stat without spawning the
    // same generic aura once per chained withBuff().
    const hasExplicitVfx = configs.buffConfigs.some((config) => config.vfx?.effectId);
    configs.buffConfigs.forEach((config, index) => {
      const BuffBehaviorClass = getBehaviorClass('buff');
      behaviors.push(new BuffBehaviorClass(
        config.stat as any,
        config.value,
        config.duration,
        // Explicit `withBuff({ vfx })` — must reach the behavior or the buff's
        // status VFX (and BuffBar icon) silently never fires.
        config.vfx,
        !hasExplicitVfx && index === 0,
      ));
    });
  }

  if (configs.aoeConfig) {
    behaviors.push(new (getBehaviorClass('aoe'))(configs.aoeConfig));
  }

  if (configs.dashConfig) {
    behaviors.push(new (getBehaviorClass('dash'))(configs.dashConfig));
  }

  if (configs.channelConfig) {
    behaviors.push(new (getBehaviorClass('channel'))(configs.channelConfig));
  }

  if (configs.healConfig) {
    behaviors.push(new (getBehaviorClass('heal'))(configs.healConfig));
  }

  if (configs.shieldConfig) {
    behaviors.push(new (getBehaviorClass('shield'))(configs.shieldConfig));
  }

  if (configs.debuffConfig) {
    behaviors.push(new (getBehaviorClass('debuff'))(configs.debuffConfig));
  }

  if (configs.meleeConfig) {
    behaviors.push(new (getBehaviorClass('melee'))(configs.meleeConfig));
  }

  if (configs.hitscanConfig) {
    behaviors.push(new (getBehaviorClass('hitscan'))(configs.hitscanConfig));
  }

  if (configs.summonConfig) {
    behaviors.push(new (getBehaviorClass('summon'))(configs.summonConfig));
  }

  if (configs.novaConfig) {
    behaviors.push(new (getBehaviorClass('novaProjectile'))(configs.novaConfig));
  }

  if (configs.auraEffectConfig) {
    behaviors.push(new (getBehaviorClass('auraEffect'))(configs.auraEffectConfig));
  }

  if (configs.coneAOEConfig) {
    const coneConfig = configs.coneAOEConfig;
    behaviors.push({
      execute: (ctx: ActionContext, _meta: AbilityMetadata) => {
        const player = ctx.currentPlayer;
        const facingRight = player.facing === 'right';
        const damage = typeof coneConfig.damage === 'function' ? coneConfig.damage(ctx) : coneConfig.damage;
        // COMBAT-MELEE-CONE-04 — the wedge is cast from the BODY centre. Its
        // apex IS that centre (plan 29 bug 5): the server resolves a cone from
        // the origin it is sent, and an origin half the reach ahead left
        // everything nearer than that behind the apex (Howl: 0 at 80 px).
        const casterAt = playerCombatPoint(player);
        eventBus.emit('combat:area_hit', {
          attackerId: player.id, damage: Math.floor(damage),
          x: casterAt.x, y: casterAt.y, radius: coneConfig.distance,
          // E3 fix: carry the ability id so DamageBridge routes the AOE under the
          // real ability (was falling back to the literal 'area_hit', which the
          // server damageNPC handler can reject → silent no-damage cone).
          abilityId: _meta.id,
          shape: 'cone', angle: coneConfig.angle, facing: facingRight ? 'right' : 'left',
        });
        // Plan 29 (lane BS): drawn from the same apex — the body centre — at
        // the cone's real reach, with the wedge the server tests washed under
        // it. Plan 29 F5: through the shared plan the remote screen draws too.
        emitVisualPlan(conePlan({ angle: coneConfig.angle, distance: coneConfig.distance, effectId: coneConfig.vfxId }, _meta.id, { body: casterAt, facingRight }));
      }
    });
  }

  if (configs.lineAOEConfig) {
    const lineConfig = configs.lineAOEConfig;
    behaviors.push({
      execute: (ctx: ActionContext, _meta: AbilityMetadata) => {
        const player = ctx.currentPlayer;
        const facingRight = player.facing === 'right';
        const damage = typeof lineConfig.damage === 'function' ? lineConfig.damage(ctx) : lineConfig.damage;
        // COMBAT-MELEE-CONE-04 — the line is cast from the BODY centre.
        const casterAt = playerCombatPoint(player);
        const hitX = casterAt.x + (facingRight ? lineConfig.length * 0.5 : -lineConfig.length * 0.5);
        eventBus.emit('combat:area_hit', {
          attackerId: player.id, damage: Math.floor(damage),
          x: hitX, y: casterAt.y, radius: lineConfig.length,
          // E3 fix: carry the ability id (see cone branch above).
          abilityId: _meta.id,
          shape: 'line', width: lineConfig.width, facing: facingRight ? 'right' : 'left',
        });
        emitVisualPlan(linePlan({ length: lineConfig.length, width: lineConfig.width, effectId: lineConfig.vfxId }, { x: hitX, y: casterAt.y }));
      }
    });
  }

  if (configs.multiProjectiles) {
    behaviors.push(new (getBehaviorClass('multiProjectile'))(
      configs.multiProjectiles.count,
      configs.multiProjectiles.stagger
    ));
  } else if (configs.projectileConfig) {
    behaviors.push(new (getBehaviorClass('projectile'))());
  }

  // Travel-mode dash (Charge/Heroic Leap): defer every behavior composed
  // AFTER the dash (and its area, if any) until arrival, so e.g. Charge's melee strike lands at the
  // destination instead of whiffing at the cast origin. Instant dashes
  // (travelMs absent/0) keep the synchronous chain.
  // A travel dash's buffs start on arrival too (Divine Steed: the gallop,
  // then its run-speed buff, D23) — they are composed before the dash above.
  if (configs.dashConfig && (configs.dashConfig.travelMs ?? 0) > 0) {
    const DashBehaviorClass = getBehaviorClass('dash');
    const BuffBehaviorClass = getBehaviorClass('buff');
    const AOEBehaviorClass = getBehaviorClass('aoe');
    const dashIdx = behaviors.findIndex((b) => b instanceof DashBehaviorClass);
    if (dashIdx >= 0) {
      const dash = behaviors[dashIdx];
      const deferred = behaviors.splice(dashIdx + 1);
      const buffs = behaviors.filter((b) => b instanceof BuffBehaviorClass);
      // Plan 29 (lane BX): an area composed with a travel dash is its landing
      // slam (Heroic Leap) — it hits and is ringed where the dash ends, not
      // at the take-off (AOE is composed before the dash above).
      const areas = behaviors.filter((b) => b instanceof AOEBehaviorClass);
      if (buffs.length > 0 || areas.length > 0 || deferred.length > 0) {
        for (const moved of [...buffs, ...areas]) behaviors.splice(behaviors.indexOf(moved), 1);
        (dash as any).setArrivalBehaviors([...buffs, ...areas, ...deferred]);
      }
    }
  }

  return behaviors;
}

// =============================================================================
// METADATA POPULATION
// =============================================================================

/**
 * Populate metadata.behaviors array for dashboards/introspection.
 */
export function populateBehaviorMetadata(
  metadata: Partial<AbilityMetadata>,
  configs: BuilderConfigs,
  extras: GeometryExtras = {},
): void {
  // Plan 29 F5: what the cast draws, described from these same configs — the
  // caster's behaviours draw from it and observers draw its snapshot.
  metadata.castGeometry = describeCastGeometry(configs, metadata.melee, { ...extras, orbit: metadata.castOrbit, abilityId: metadata.id, projectile: metadata.projectile });
  metadata.behaviors = [];
  if (configs.instantEffect) metadata.behaviors.push({ type: 'instant', params: {} });
  for (const buffConfig of configs.buffConfigs) {
    metadata.behaviors.push({ type: 'buff', params: { stat: buffConfig.stat } });
  }
  if (configs.aoeConfig) metadata.behaviors.push({ type: 'aoe', params: { radius: configs.aoeConfig.radius, placement: configs.aoeConfig.placement } });
  if (configs.dashConfig) metadata.behaviors.push({ type: 'dash', params: { distance: configs.dashConfig.distance, travelMs: configs.dashConfig.travelMs ?? 0 } });
  if (configs.channelConfig) metadata.behaviors.push({ type: 'channel', params: {} });
  if (configs.healConfig) metadata.behaviors.push({ type: 'heal', params: {} });
  if (configs.shieldConfig) metadata.behaviors.push({ type: 'shield', params: {} });
  if (configs.debuffConfig) metadata.behaviors.push({ type: 'debuff', params: {} });
  if (configs.meleeConfig) metadata.behaviors.push({ type: 'melee', params: {} });
  if (configs.hitscanConfig) metadata.behaviors.push({ type: 'hitscan', params: {} });
  if (configs.summonConfig) metadata.behaviors.push({ type: 'summon', params: { summonType: configs.summonConfig.type, placement: configs.summonConfig.placement, radius: configs.summonConfig.radius } });
  if (configs.novaConfig) metadata.behaviors.push({ type: 'nova', params: { count: configs.novaConfig.count } });
  if (configs.auraEffectConfig) metadata.behaviors.push({ type: 'aura', params: {} });
  if (configs.coneAOEConfig) metadata.behaviors.push({ type: 'cone_aoe', params: {} });
  if (configs.lineAOEConfig) metadata.behaviors.push({ type: 'line_aoe', params: {} });
  if (configs.multiProjectiles) metadata.behaviors.push({ type: 'multi_projectile', params: { count: configs.multiProjectiles.count } });
  else if (configs.projectileConfig) metadata.behaviors.push({ type: 'projectile', params: {} });
  if (configs.passiveConfig) metadata.behaviors.push({ type: 'passive', params: {} });
  if (configs.toggleConfig) metadata.behaviors.push({ type: 'toggle', params: {} });
}

/**
 * Derive targeting and mechanic type from configs for contract alignment.
 */
export function deriveTargetingAndMechanic(
  metadata: Partial<AbilityMetadata>,
  configs: BuilderConfigs,
): void {
  // Derive targeting
  if (configs.projectileConfig || configs.multiProjectiles || configs.novaConfig) {
    (metadata as any).targeting = 'projectile';
  } else if (configs.aoeConfig || configs.coneAOEConfig || configs.lineAOEConfig) {
    (metadata as any).targeting = 'aoe';
  } else if (configs.meleeConfig || configs.hitscanConfig) {
    (metadata as any).targeting = 'target';
  } else if (configs.healConfig || configs.buffConfigs.length > 0 || configs.shieldConfig) {
    (metadata as any).targeting = 'self';
  } else if (configs.summonConfig) {
    (metadata as any).targeting = 'ground';
  }

  // Derive mechanic type
  if (configs.passiveConfig) (metadata as any).mechanicType = 'passive';
  else if (configs.toggleConfig) (metadata as any).mechanicType = 'toggle';
  else if (configs.channelConfig) (metadata as any).mechanicType = 'channel';
  else if (configs.windUpDuration > 0) (metadata as any).mechanicType = 'cast';
  else (metadata as any).mechanicType = 'instant';
}
